/**
 * helpers/forms/share.ts
 * URL share helpers for Tool Forms (Base64URL-safe, UTF-8 safe, SSR-safe)
 *
 * Encodes/decodes a form payload into the URL and reads it back.
 * Supports query (?param=...) or hash (#param=...) targets.
 * Meant specifically for the "share simulation" feature.
 *
 * Includes ZERO-TOUCH AUTO-BOOT that:
 *  - Reads payload from URL
 *  - Waits for window.globalSetFieldValue (Formik setter your app exposes)
 *  - Finds the correct <form> inside [data-block="tool-block"]
 *  - Hydrates fields
 *  - Clears URL param
 *  - Submits the form (native requestSubmit → Formik onSubmit)
 *  - Fallback: clicks the "Simular" button if needed
 *  - Does NOT work on article/post pages to prevent unwanted scroll
 */

type AnyObject = Record<string, any>;

export type Target = "query" | "hash";

export type WriteOptions = {
  paramName?: string;     // default: "data"
  replace?: boolean;      // default: true
  pruneEmpty?: boolean;   // default: true
  deep?: boolean;         // default: true
  target?: Target;        // default: "query"
};

export type ReadOptions = {
  paramName?: string;     // default: "data"
  target?: Target;        // default: "query"
};

export type ClearOptions = {
  paramName?: string;     // default: "data"
  replace?: boolean;      // default: true
  target?: Target;        // default: "query"
};

const DEFAULT_PARAM = "data";

/* -----------------------------------------------------------
 * Base64URL helpers (UTF-8 safe)
 * --------------------------------------------------------- */

const encodeBase64Url = (str: string): string => {
  const utf8 = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
    String.fromCharCode(parseInt(p1, 16))
  );
  const b64 =
    typeof btoa === "function"
      ? btoa(utf8)
      : Buffer.from(utf8, "binary").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const decodeBase64Url = (input: string): string => {
  let b64 = input.replace(/\s/g, '').replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad);

  const bin =
    typeof atob === "function"
      ? atob(b64)
      : Buffer.from(b64, "base64").toString("binary");

  const percentEncoded = Array.prototype.map
    .call(bin, (c: string) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
    .join("");
  return decodeURIComponent(percentEncoded);
};

/* -----------------------------------------------------------
 * JSON helpers
 * --------------------------------------------------------- */

const toJson = (obj: AnyObject): string => JSON.stringify(obj);
const fromJson = (json: string): AnyObject => JSON.parse(json);

/* -----------------------------------------------------------
 * Pruning helpers
 * --------------------------------------------------------- */

const isEmptyLike = (v: unknown) => v === "" || v === undefined || v === null;

const shallowPrune = (obj: AnyObject): AnyObject => {
  const out: AnyObject = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (isEmptyLike(v)) return;
    out[k] = v;
  });
  return out;
};

const deepPrune = (obj: any): any => {
  if (Array.isArray(obj)) {
    const arr = obj.map(deepPrune).filter((v) => !isEmptyLike(v));
    return arr;
  }
  if (obj && typeof obj === "object") {
    const out: AnyObject = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (isEmptyLike(v)) return;
      const cleaned = typeof v === "object" ? deepPrune(v) : v;
      if (!isEmptyLike(cleaned)) out[k] = cleaned;
    });
    return out;
  }
  return obj;
};

/* -----------------------------------------------------------
 * Browser/SSR guards
 * --------------------------------------------------------- */

const hasWindow = () => typeof window !== "undefined";
const hasHistory = () => hasWindow() && typeof history !== "undefined";

/* -----------------------------------------------------------
 * Hash helpers
 * --------------------------------------------------------- */

const getHashParams = (): URLSearchParams => {
  const hash = (window.location.hash || "").replace(/^#/, "");
  return new URLSearchParams(hash);
};

const setHashParams = (sp: URLSearchParams, replace: boolean): void => {
  const frag = sp.toString();
  const url = new URL(window.location.href);
  url.hash = frag ? `#${frag}` : "";
  if (hasHistory()) {
    if (replace) {
      history.replaceState({}, "", url.toString());
    } else {
      history.pushState({}, "", url.toString());
    }
  } else {
    window.location.replace(url.toString());
  }
};

/* -----------------------------------------------------------
 * Public API
 * --------------------------------------------------------- */

export const writeSharedToUrl = (
  values: AnyObject,
  opts: WriteOptions = {}
): void => {
  if (!hasWindow()) return;

  const {
    paramName = DEFAULT_PARAM,
    replace = true,
    pruneEmpty = true,
    deep = true,
    target = "query",
  } = opts;

  try {
    const cleaned = pruneEmpty ? (deep ? deepPrune(values) : shallowPrune(values)) : values;
    const encoded = encodeBase64Url(toJson(cleaned));

    if (target === "hash") {
      const sp = getHashParams();
      sp.set(paramName, encoded);
      setHashParams(sp, replace);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set(paramName, encoded);

    if (hasHistory()) {
      if (replace) {
        history.replaceState({}, "", url.toString());
      } else {
        history.pushState({}, "", url.toString());
      }
    } else {
      window.location.replace(url.toString());
    }
  } catch (e) {
    console.error("[share] Failed to write values to URL:", e);
  }
};

export const readSharedFromUrl = (opts: ReadOptions = {}): AnyObject | null => {
  if (!hasWindow()) return null;

  const { paramName = DEFAULT_PARAM, target = "query" } = opts;

  try {
    const raw =
      target === "hash"
        ? getHashParams().get(paramName)
        : new URLSearchParams(window.location.search).get(paramName);

    if (!raw) return null;

    const json = decodeBase64Url(raw);
    const obj = fromJson(json);
    return obj && typeof obj === "object" ? obj : null;
  } catch (e) {
    console.error("[share] Failed to read values from URL:", e);
    return null;
  }
};

export const hasSharedInUrl = (opts: ReadOptions = {}): boolean => {
  if (!hasWindow()) return false;
  const { paramName = DEFAULT_PARAM, target = "query" } = opts;

  if (target === "hash") {
    return getHashParams().has(paramName);
  }
  const params = new URLSearchParams(window.location.search);
  return params.has(paramName);
};

export const clearSharedFromUrl = (opts: ClearOptions = {}): void => {
  if (!hasWindow()) return;

  const { paramName = DEFAULT_PARAM, replace = true, target = "query" } = opts;
  try {
    if (target === "hash") {
      const sp = getHashParams();
      sp.delete(paramName);
      setHashParams(sp, replace);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete(paramName);

    if (hasHistory()) {
      if (replace) {
        history.replaceState({}, "", url.toString());
      } else {
        history.pushState({}, "", url.toString());
      }
    } else {
      window.location.replace(url.toString());
    }
  } catch (e) {
    console.error("[share] Failed to clear shared payload from URL:", e);
  }
};

export const mergeWithDefaults = (
  defaults: AnyObject,
  payload: AnyObject | null
): AnyObject => {
  if (!payload) return defaults || {};
  return { ...(defaults || {}), ...payload };
};

/* -----------------------------------------------------------
 * Programmatic hydrate & submit (exported utility)
 * --------------------------------------------------------- */

export type HydrateAndSubmitOptions = {
  paramName?: string;      // default: "data"
  target?: Target;         // default: "query"
  clearAfter?: boolean;    // default: true
  clearReplace?: boolean;  // default: true
  mergeDefaults?: boolean; // default: true
  submitDelayMs?: number;  // default: 0
  validateOnSet?: boolean; // default: false
  validate?: () => Promise<any> | void;
  onBeforeSubmit?: (values: AnyObject) => boolean | Promise<boolean>;
  debug?: boolean;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const nextFrame = () =>
  new Promise<void>((r) =>
    (typeof requestAnimationFrame === "function"
      ? requestAnimationFrame(() => r())
      : setTimeout(() => r(), 0))
  );

export const hydrateAndSubmitFromUrl = async (
  defaults: AnyObject,
  setValues: (vals: AnyObject, shouldValidate?: boolean) => any,
  submit: () => any,
  opts: HydrateAndSubmitOptions = {}
): Promise<boolean> => {
  if (!hasWindow()) return false;

  const {
    paramName = DEFAULT_PARAM,
    target = "query",
    clearAfter = true,
    clearReplace = true,
    mergeDefaults = true,
    submitDelayMs = 0,
    validateOnSet = false,
    validate,
    onBeforeSubmit,
    debug = false,
  } = opts;

  try {
    const payload = readSharedFromUrl({ paramName, target });
    if (!payload) {
      if (debug) console.debug("[share] No payload found in URL");
      return false;
    }

    const merged = mergeDefaults ? mergeWithDefaults(defaults, payload) : payload;

    if (debug) console.debug("[share] Hydrating with:", merged);
    setValues(merged, validateOnSet);

    if (clearAfter) {
      if (debug) console.debug("[share] Clearing URL param");
      clearSharedFromUrl({ paramName, target, replace: clearReplace });
    }

    await Promise.resolve();
    await nextFrame();
    if (submitDelayMs > 0) await sleep(submitDelayMs);

    if (validate) {
      if (debug) console.debug("[share] Running validate() before submit");
      await validate();
    }

    if (onBeforeSubmit) {
      if (debug) console.debug("[share] onBeforeSubmit hook");
      const proceed = await onBeforeSubmit(merged);
      if (!proceed) return true;
    }

    if (debug) console.debug("[share] Submitting…");
    await submit();
    if (debug) console.debug("[share] Submit invoked");

    return true;
  } catch (e) {
    console.error("[share] hydrateAndSubmitFromUrl failed:", e);
    return false;
  }
};

/* -----------------------------------------------------------
 * ZERO-TOUCH AUTO-BOOT (no component edits)
 * --------------------------------------------------------- */

/**
 * Config
 */
const AUTOBOOT = {
  enabled: true,
  paramName: DEFAULT_PARAM,
  prefer: "hash" as Target,
  fallback: "query" as Target,
  waitSetterTimeoutMs: 6000,  // a bit more generous
  waitSetterIntervalMs: 50,
  submitDelayMs: 80,          // a tiny breath for widgets to bind
  // we now strictly scope to the tool block, with a safe fallback:
  formSelectorScoped: '[data-block="tool-block"] form',
  formSelectorFallback: 'form',
  // try to click the "Simular" button if native submit didn't fire:
  submitButtonSelector: '.tool-form__form__field__submit button',
  // Only auto-submit if block attributes say enableShareSimulation === true?
  requireEnableFlag: false,   // set to true if you want to honor the flag below
  debug: false,
};

/**
 * Helpers
 */
const waitForGlobalSetFieldValue = async (
  timeoutMs: number,
  intervalMs: number,
): Promise<((name: string, value: any, shouldValidate?: boolean) => void) | null> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const setter = (window as any).globalSetFieldValue;
    if (typeof setter === "function") return setter;
    await sleep(intervalMs);
  }
  return null;
};

const waitForElement = async (selector: string, timeoutMs: number, intervalMs: number): Promise<Element | null> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const el = document.querySelector(selector);
    if (el) return el;
    await sleep(intervalMs);
  }
  return null;
};

const findToolForm = async (): Promise<HTMLFormElement | null> => {
  const scoped = (await waitForElement(AUTOBOOT.formSelectorScoped, 2000, 50)) as HTMLFormElement | null;
  if (scoped) return scoped;
  // Fallback to the FIRST real form in the page (but avoid newsletter/login forms by preferring longer forms)
  const fallback = (await waitForElement(AUTOBOOT.formSelectorFallback, 2000, 50)) as HTMLFormElement | null;
  return fallback;
};

const submitNativeForm = (form: HTMLFormElement | null) => {
  if (!form) return false;
  if (typeof form.requestSubmit === "function") {
    form.requestSubmit();
    return true;
  }
  const evt =
    typeof SubmitEvent !== "undefined"
      ? new SubmitEvent("submit", { bubbles: true, cancelable: true })
      : new Event("submit", { bubbles: true, cancelable: true });
  return form.dispatchEvent(evt);
};

/**
 * Auto-boot routine
 */
const autoBoot = async () => {
  if (!hasWindow() || !AUTOBOOT.enabled) return;

  // Optional: honor the enable flag in the block attributes
  if (AUTOBOOT.requireEnableFlag) {
    const root = document.querySelector('[data-block="tool-block"]') as HTMLElement | null;
    if (root?.dataset?.attributes) {
      try {
        const attrs = JSON.parse(root.dataset.attributes);
        if (attrs?.enableShareSimulation !== true) return;
      } catch {
        // ignore parse error → proceed
      }
    }
  }

  // 1) Find payload (hash first, then query)
  let targetUsed: Target | null = null;
  let payload: AnyObject | null = null;

  if (hasSharedInUrl({ paramName: AUTOBOOT.paramName, target: AUTOBOOT.prefer })) {
    targetUsed = AUTOBOOT.prefer;
    payload = readSharedFromUrl({ paramName: AUTOBOOT.paramName, target: targetUsed });
  } else if (hasSharedInUrl({ paramName: AUTOBOOT.paramName, target: AUTOBOOT.fallback })) {
    targetUsed = AUTOBOOT.fallback;
    payload = readSharedFromUrl({ paramName: AUTOBOOT.paramName, target: targetUsed });
  }

  if (!payload || !targetUsed) return;

  // 2) Wait for the Formik setter
  const setter = await waitForGlobalSetFieldValue(
    AUTOBOOT.waitSetterTimeoutMs,
    AUTOBOOT.waitSetterIntervalMs
  );
  if (!setter) {
    return;
  }

  // 3) Find the correct form (scoped first)
  const form = await findToolForm();
  if (!form) {
    return;
  }

  // 4) Hydrate every key (flat keys expected; nested paths also work)
  try {
    Object.entries(payload).forEach(([name, value]) => {
      try {
        setter(name, value, false); // do not validate on set
      } catch (e) {
        // Silently ignore field errors
      }
    });
  } catch (e) {
    return;
  }

  // 5) Clear URL param immediately to avoid re-triggers on navigation
  try {
    clearSharedFromUrl({ paramName: AUTOBOOT.paramName, target: targetUsed, replace: true });
  } catch (e) {
    // Silently ignore
  }

  // 6) Let React/Formik + widgets flush/bind
  await Promise.resolve();
  await nextFrame();
  if (AUTOBOOT.submitDelayMs > 0) await sleep(AUTOBOOT.submitDelayMs);

  // 7) Submit the form; fallback to clicking the Simular button if needed
  let submitted = submitNativeForm(form);
  if (!submitted) {
    const btn =
      (document.querySelector('[data-block="tool-block"]') as HTMLElement | null)?.querySelector(
        AUTOBOOT.submitButtonSelector
      ) ||
      document.querySelector(AUTOBOOT.submitButtonSelector);
    if (btn instanceof HTMLButtonElement && !btn.disabled) {
      btn.click();
      submitted = true;
    }
  }

};

// Kick off after DOM is ready (browser only)
if (hasWindow()) {
  const start = () => setTimeout(autoBoot, 0);
  if (document.readyState === "complete" || document.readyState === "interactive") {
    start();
  } else {
    window.addEventListener("DOMContentLoaded", start, { once: true });
  }
}
